// src/pages/About.jsx
import React from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Chip, Button, Avatar, Stack, Paper, Divider, alpha,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  Public, VerifiedUser, Security, SupportAgent, TrendingUp,
  GppGood, RocketLaunch, ArrowRightAlt, Business, Email,
  Phone, LocationOn, EmojiEvents, Groups, Handshake, Favorite,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
});

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const stats = [
    { value: '500+', label: 'Verified Suppliers', icon: '🏭' },
    { value: '150+', label: 'Countries Served', icon: '🌍' },
    { value: '$2B+', label: 'Annual Trade Volume', icon: '💰' },
    { value: '5,000+', label: 'Active Traders', icon: '👥' },
    { value: '99%', label: 'Quality Pass Rate', icon: '⭐' },
    { value: '48h', label: 'Customs Clearance', icon: '📄' },
  ];

  const values = [
    { icon: <Security sx={{ fontSize: 36 }} />, color: '#2196f3', title: 'Trust & Transparency', desc: 'Every transaction on our platform is backed by verified identities, certified quality inspections, and escrow-protected payments.' },
    { icon: <GppGood sx={{ fontSize: 36 }} />, color: '#4caf50', title: 'Quality First', desc: 'We maintain Grade A standards with rigorous pre-shipment inspections and phytosanitary certifications for all produce.' },
    { icon: <Public sx={{ fontSize: 36 }} />, color: '#ff9800', title: 'Global Accessibility', desc: 'Enabling small and medium-sized fruit businesses to trade globally with the same tools as large corporations.' },
    { icon: <Handshake sx={{ fontSize: 36 }} />, color: '#e91e63', title: 'Partnership Driven', desc: 'We build long-term relationships — not one-time transactions — between buyers, sellers, and logistics partners.' },
    { icon: <TrendingUp sx={{ fontSize: 36 }} />, color: '#9c27b0', title: 'Continuous Innovation', desc: 'Constantly improving our platform with AI-driven matchmaking, real-time tracking, and smart pricing tools.' },
    { icon: <Favorite sx={{ fontSize: 36 }} />, color: '#00bcd4', title: 'Community Impact', desc: 'Supporting thousands of farmers and exporters in developing nations by connecting them to premium global markets.' },
  ];

  const milestones = [
    { year: '2019', event: 'Smartwave International Pvt. Ltd. founded in Chennai, Tamil Nadu' },
    { year: '2020', event: 'First 50 verified exporters onboarded; platform launched for South Asian trade' },
    { year: '2021', event: 'Expanded to Middle East and European markets; 500+ traders joined' },
    { year: '2022', event: 'Launched escrow payment system and real-time shipment tracking' },
    { year: '2023', event: '$1B+ in trade volume processed; 100+ countries served' },
    { year: '2024', event: '5,000+ active traders, 500+ verified suppliers, 150+ countries' },
    { year: '2025', event: 'AI-powered matchmaking and predictive pricing launched' },
  ];

  return (
    <Box sx={{ backgroundColor: 'grey.50', minHeight: '100vh' }}>

      {/* ── Hero ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0d2f12 0%, #1b5e20 40%, #2e7d32 100%)',
        color: 'white', py: { xs: 8, md: 14 }, position: 'relative', overflow: 'hidden',
      }}>
        {[300, 500, 200].map((size, i) => (
          <Box key={i} sx={{
            position: 'absolute', borderRadius: '50%',
            backgroundColor: alpha('#4caf50', 0.07),
            width: size, height: size,
            top: ['-10%', '30%', '60%'][i], left: ['65%', '-8%', '75%'][i],
            pointerEvents: 'none',
          }} />
        ))}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <motion.div {...fadeUp()}>
                <Chip label="ABOUT US" sx={{ bgcolor: alpha('#ffeb3b', 0.2), color: '#ffeb3b', fontWeight: 700, mb: 3 }} />
                <Typography variant="h2" fontWeight={800} sx={{ mb: 3, fontSize: { xs: '2rem', md: '3.5rem' }, lineHeight: 1.15 }}>
                  Connecting the World's
                  <Box component="span" sx={{ background: 'linear-gradient(90deg,#ffeb3b,#ff9800)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'block' }}>
                    Fruit Markets
                  </Box>
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.88, fontWeight: 400, mb: 4, maxWidth: 560, lineHeight: 1.7 }}>
                  Smartwave International Private Limited is a Chennai-based global fruit export platform, 
                  bridging verified suppliers and buyers across 150+ countries with trust, technology, and transparency.
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                  <Button component={Link} to="/register" variant="contained" size="large" startIcon={<RocketLaunch />}
                    sx={{ bgcolor: '#ffeb3b', color: '#1b5e20', fontWeight: 'bold', borderRadius: 3, px: 4 }}>
                    Join the Platform
                  </Button>
                  <Button component={Link} to="/contact" variant="outlined" size="large" endIcon={<ArrowRightAlt />}
                    sx={{ borderColor: alpha('#fff', 0.4), color: 'white', borderRadius: 3, px: 4 }}>
                    Get in Touch
                  </Button>
                </Stack>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={5}>
              <motion.div {...fadeUp(0.2)}>
                <Paper sx={{
                  p: 4, borderRadius: 4,
                  backgroundColor: alpha('#fff', 0.08),
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}>
                  <Typography variant="h6" fontWeight="bold" color="white" gutterBottom sx={{ mb: 3 }}>
                    🏢 Smartwave International Pvt. Ltd.
                  </Typography>
                  <Stack spacing={2.5}>
                    {[
                      { icon: <LocationOn />, label: 'Old No: 55, New No: 123, Eldams Road, Teynampet, Chennai – 600018, Tamil Nadu, India' },
                      { icon: <Email />, label: 'Support@smartwaveintl.com' },
                      { icon: <Phone />, label: '+91 8925851195 / +91 8925851196' },
                    ].map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{ color: '#ffeb3b', mt: 0.2, flexShrink: 0 }}>{item.icon}</Box>
                        <Typography variant="body2" sx={{ color: alpha('#fff', 0.9), lineHeight: 1.6 }}>{item.label}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Stats ── */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {stats.map((stat, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <motion.div {...fadeUp(i * 0.07)}>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h2" sx={{ mb: 0.5 }}>{stat.icon}</Typography>
                    <Typography variant="h4" fontWeight={800} color="primary.main">{stat.value}</Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>{stat.label}</Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Our Story ── */}
      <Container maxWidth="xl" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div {...fadeUp()}>
              <Chip label="OUR STORY" sx={{ bgcolor: alpha('#4caf50', 0.1), color: 'primary.main', fontWeight: 700, mb: 2 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                From Chennai to the World
              </Typography>
              <Stack spacing={2}>
                {[
                  'Smartwave International Private Limited was founded in 2019 in Chennai, Tamil Nadu, with a single mission: to make global fruit trading simple, secure, and accessible for everyone.',
                  'We saw a massive gap — thousands of quality fruit exporters in India and beyond had no reliable way to connect with international buyers. Import processes were opaque, payments were risky, and quality verification was inconsistent.',
                  'Our founding team, with deep expertise in agri-export, supply chain, and technology, built a platform that brings together verified exporters, trusted logistics partners, and certified inspectors under one roof.',
                  'Today, Smartwave powers $2B+ in annual trade across 150+ countries, serving 5,000+ active traders who rely on our platform for every step — from enquiry to delivery.',
                ].map((para, i) => (
                  <Typography key={i} variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {para}
                  </Typography>
                ))}
              </Stack>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div {...fadeUp(0.2)}>
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>Our Journey</Typography>
              <Stack spacing={0}>
                {milestones.map((m, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '0.75rem', fontWeight: 'bold', flexShrink: 0 }}>
                        {m.year.slice(2)}
                      </Avatar>
                      {i < milestones.length - 1 && (
                        <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'divider', my: 0.5, minHeight: 24 }} />
                      )}
                    </Box>
                    <Box sx={{ pb: 2.5 }}>
                      <Typography variant="caption" color="primary.main" fontWeight="bold">{m.year}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{m.event}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* ── Values ── */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <motion.div {...fadeUp()}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip label="OUR VALUES" sx={{ bgcolor: alpha('#ff9800', 0.1), color: '#ff9800', fontWeight: 700, mb: 2 }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom>What We Stand For</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                Our core principles guide every decision we make on the platform
              </Typography>
            </Box>
          </motion.div>
          <Grid container spacing={3}>
            {values.map((v, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <motion.div {...fadeUp(i * 0.08)}>
                  <Paper sx={{
                    p: 3.5, borderRadius: 4, height: '100%',
                    border: `1px solid ${alpha(v.color, 0.15)}`,
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: `0 12px 40px ${alpha(v.color, 0.15)}`, transform: 'translateY(-4px)' },
                  }} elevation={0}>
                    <Avatar sx={{ bgcolor: alpha(v.color, 0.12), color: v.color, width: 64, height: 64, mb: 2 }}>
                      {v.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{v.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{v.desc}</Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Container maxWidth="xl" sx={{ py: 10 }}>
        <motion.div {...fadeUp()}>
          <Paper sx={{
            background: 'linear-gradient(135deg,#1b5e20 0%,#2e7d32 100%)',
            color: 'white', p: { xs: 5, md: 8 }, borderRadius: 4, textAlign: 'center',
          }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Be Part of Our Story
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 4, maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
              Join Smartwave International and grow your fruit business across the globe.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button component={Link} to="/register" variant="contained" size="large" startIcon={<RocketLaunch />}
                sx={{ bgcolor: '#ffeb3b', color: '#1b5e20', fontWeight: 'bold', borderRadius: 3, px: 5 }}>
                Start Trading Today
              </Button>
              <Button component={Link} to="/contact" variant="outlined" size="large" endIcon={<ArrowRightAlt />}
                sx={{ borderColor: alpha('#fff', 0.5), color: 'white', borderRadius: 3, px: 5 }}>
                Contact Us
              </Button>
            </Stack>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About;